// @ts-nocheck

import cn from 'classnames';
import { intersection, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingSkeleton, Selector } from 'sonata-react-components';

import { reportingInsightsSetGroupByConfig } from '@/store/actions/reporting.actions';
import { reportAdvancedFilterSetInsightCountry } from '@/store/actions/reportingAdvancedFilter.actions';
import { getCartographyCountries } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';

import './boundaryShape.scss';

function BoundaryShape({ countries, insightId, onFilter, section, ...props }) {
  const [{ countryOptions, defaultLevel, options, selected, selectedCountries, status }, setState] = useState({
    countryOptions: [],
    options: [],
    selected: {},
    selectedCountries: {},
    status: 'idle'
  });

  const generalCountries = useSelector(store => getCartographyCountries(store, 'general'));
  const sectionCountries = useSelector(store => getCartographyCountries(store, section));

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const dispatchCountry = selectedCountries => {
    const countryCodes = selectedCountries.map(({ id }) => id);
    dispatch(reportAdvancedFilterSetInsightCountry(countryCodes, insightId));
  };

  const showCountrySelector = countries.length > 1 && generalCountries.length === 0 && sectionCountries.length === 0;

  const demographyToken = {};
  const isPending = status === 'pending';

  useEffect(() => {
    return () => demographyToken.cancel && demographyToken.cancel();
  }, []);

  const getOptions = async () => {
    setState(currentState => ({
      ...currentState,
      status: 'pending'
    }));
    try {
      const result = await getCartographyDictionary(countries, demographyToken);
      const countryOptionsResult = result.map(country => ({
        id: country.id,
        label: country.name
      }));
      const merged = result
        .reduce(
          (accCountry, currentCountry) => [
            ...accCountry,
            ...currentCountry.tagsConfig.map(tag => ({
              ...tag,
              country: currentCountry.id
            }))
          ],
          []
        )
        .filter(tag => tag.reportingMapEventsEnable && tag.id !== 'level0')
        .map(item => ({
          country: item.country,
          id: item.id,
          isDisabled: undefined,
          label: item.name,
          value: item.id
        }));

      setState(currentState => ({
        ...currentState,
        countryOptions: countryOptionsResult.sort((a, b) => a.label.localeCompare(b.label)),
        defaultLevel: merged[0]?.id || 'level0',
        options: merged,
        selectedCountries: countryOptionsResult,
        status: 'success'
      }));

      if (countryOptionsResult.length <= 1) {
        handleOnChange(merged[0] || 'level0');
      }
    } catch (e) {
      console.info(e.message);
    }
  };

  useEffect(() => {
    getOptions();
  }, []);

  const handleOnChange = item => {
    setState(currentState => ({
      ...currentState,
      selected: item
    }));
    onFilter(item);
    dispatch(
      reportingInsightsSetGroupByConfig({
        insightGroupByConfig: { groupBy: item },
        insightId
      })
    );
  };
  const handleOnChangeCountry = (items, optionsMerged) => {
    const countriesToSelect = items.length ? items : [countryOptions[0]];
    const filteredOptions = (optionsMerged || options).filter(option =>
      countriesToSelect.find(country => country.id === option.country)
    );
    const levelOptionToSelect =
      countriesToSelect.length > 1
        ? {
            country: null,
            id: 'level0',
            value: 'level0'
          }
        : filteredOptions[0];
    setState(currentState => ({
      ...currentState,
      selected: levelOptionToSelect,
      selectedCountries: countriesToSelect
    }));
    dispatchCountry(countriesToSelect);
    onFilter(levelOptionToSelect);
  };

  const getDefaultValue = () => {
    const defaultLevelItem = options.filter(option => option.id === defaultLevel);
    if (defaultLevelItem) {
      return defaultLevelItem;
    }
    return options.filter(option => option.id === 'level0');
  };

  const defaultValue = useMemo(() => (options.length > 0 ? getDefaultValue() : []), [options]);
  const levelOptions = options.filter(option => selectedCountries.find(country => country.id === option.country));

  useEffect(() => {
    if (defaultValue.length) {
      onFilter({
        ...defaultValue[0],
        isDisabled: false,
        value: defaultValue[0].id
      });
    }
  }, [defaultValue]);

  const boundaryShapeClassName = cn('boundaryShape', {
    boundaryShape__multiple: showCountrySelector
  });

  return (
    <>
      {isPending ? (
        <div className="boundaryShape__skeleton">
          <LoadingSkeleton className="boundaryShape__skeleton-label" count={1} delay={2} duration={2} width="25%" />
          <LoadingSkeleton className="boundaryShape__skeleton-selector" count={1} delay={2} duration={2} width="100%" />
        </div>
      ) : (
        <div className={boundaryShapeClassName}>
          {showCountrySelector && (
            <Selector
              className="boundaryShape__selector"
              // defaultValue={defaultCountryValue}
              onClose={handleOnChangeCountry}
              options={countryOptions}
              type="multiple"
              value={isEmpty(selectedCountries) ? undefined : selectedCountries}
              {...props}
              label={formatMessage({
                defaultMessage: 'Country',
                id: 'insights.boundaryShape.label'
              })}
            />
          )}
          {selectedCountries.length <= 1 && (
            <Selector
              className="boundaryShape__selector"
              defaultValue={defaultValue}
              isDisabled={selected?.id === 'level0'}
              onChange={handleOnChange}
              options={levelOptions}
              type="simple"
              value={isEmpty(selected) ? undefined : selected}
              {...props}
            />
          )}
        </div>
      )}
    </>
  );
}

BoundaryShape.propTypes = {
  countries: PropTypes.array.isRequired,
  defaultLevel: PropTypes.string,
  insightId: PropTypes.string,
  onFilter: PropTypes.func,
  section: PropTypes.oneOf([
    'campaignsProgress',
    'general',
    'insights',
    'map',
    'summary',
    'table',
    'footfallAttribution'
  ]),
  showCountrySelector: PropTypes.bool
};

export default BoundaryShape;
